import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Lock } from 'lucide-react';

/**
 * Read-only review of a Railcar Repair Status Notification snapshot.
 * Operator+ may enter a Facility Representative Name to sign off and
 * approve. Approving flips the snapshot to APPROVED and unlocks PDF
 * download. There is no editing here.
 */
const Row = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-2 py-1">
    <div className="text-sm font-medium text-[#5A5A5A]">{k}</div>
    <div className="col-span-2 text-sm text-[#323033]">{v || '-'}</div>
  </div>
);

const RailcarRepairStatusNotificationReviewModal = ({
  open,
  onOpenChange,
  snapshot,
  canApprove,
  onApproved,
}) => {
  const { api } = useAuth();
  const [rep, setRep] = useState('');
  const [questions, setQuestions] = useState('');
  const [busy, setBusy] = useState(false);

  if (!snapshot) return null;

  const isApproved = snapshot.state === 'APPROVED';
  const fmt = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  const REPAIR_LABEL = {
    PERMANENT: 'Permanent Repair',
    TEMPORARY: 'Temporary Repair',
    PREPPED_FOR_HOME_SHOP: 'Prepped for Home Shop',
    N_A: 'N/A',
  };
  const LOAD_LABEL = {
    CLEAN: 'Clean', EMPTY: 'Empty', RESIDUE: 'Residue', LOADED: 'Loaded', OTHER: 'Other',
  };

  const approve = async () => {
    if (!rep.trim()) {
      toast.error('Facility Representative Name is required to approve.');
      return;
    }
    if (!questions.trim()) {
      toast.error('"For questions, please contact:" is required to approve.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/railcar-status-notifications/${snapshot.id}/approve`,
        {
          facility_rep_name: rep.trim(),
          questions_followup_contact: questions.trim(),
        },
      );
      toast.success('Notification approved.');
      onOpenChange(false);
      if (onApproved) onApproved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve.');
    } finally {
      setBusy(false);
    }
  };

  const load = snapshot.load_status === 'OTHER' && snapshot.load_status_other
    ? `Other – ${snapshot.load_status_other}`
    : (LOAD_LABEL[snapshot.load_status] || snapshot.load_status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="rrsn-review-modal"
      >
        <DialogHeader>
          <DialogTitle>Railcar Repair Status Notification</DialogTitle>
          <DialogDescription className="text-[#323033]">
            <span className="font-semibold">{snapshot.status_wording}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="border rounded-md p-3">
          <Row k="Service Request #" v={snapshot.sr_number} />
          <Row k="Facility" v={snapshot.facility_name} />
          <Row k="Car Mark / Number" v={`${snapshot.car_mark || ''} ${snapshot.car_number || ''}`.trim()} />
          <Row k="Load Status" v={load} />
          <Row k="Repair Type" v={REPAIR_LABEL[snapshot.repair_type] || snapshot.repair_type} />
          <Row k="Car Spot" v={snapshot.car_spot} />
          <Row k="Tags / Seals Applied" v={snapshot.tags_seals_applied} />
          {snapshot.outcome === 'INCOMPLETE' && (
            <Row k="Issue Reason" v={snapshot.issue_reason} />
          )}
          <Row k="Additional Repair Notes" v={snapshot.additional_repair_notes} />
          <Row k="Site Contact Notified Name" v={snapshot.site_contact_notified_name} />
          {isApproved && (
            <Row k="For questions, please contact:" v={snapshot.questions_followup_contact} />
          )}
          <div className="border-t border-[#D3D5D4] my-2" />
          <Row k="Submitted By" v={`${snapshot.submitted_by_name || '-'}  ·  ${fmt(snapshot.submitted_at)}`} />
          {isApproved && (
            <>
              <Row k="Approved By" v={`${snapshot.approved_by_name}  ·  ${fmt(snapshot.approved_at)}`} />
              <Row k="Facility Representative" v={snapshot.facility_rep_name} />
            </>
          )}
        </div>

        {!isApproved && canApprove && (
          <div className="border rounded-md p-3 space-y-2 bg-[#F5F5F5]">
            <div className="flex items-center gap-1 text-sm font-medium text-[#323033]">
              <ShieldCheck className="w-4 h-4" /> Approved By
            </div>
            <Label htmlFor="rrsn-rep">Facility Representative Name *</Label>
            <Input
              id="rrsn-rep"
              maxLength={100}
              value={rep}
              onChange={(e) => setRep(e.target.value)}
              placeholder="Enter Facility Rep name"
              data-testid="rrsn-facility-rep-input"
            />
            <Label htmlFor="rrsn-questions">For questions, please contact: *</Label>
            <Input
              id="rrsn-questions"
              maxLength={100}
              value={questions}
              onChange={(e) => setQuestions(e.target.value)}
              placeholder="Name, phone, or email for follow-up"
              data-testid="rrsn-questions-input"
            />
            <p className="text-xs text-[#5A5A5A]">
              Approving locks the sign-off and enables PDF download. This action cannot be undone.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rrsn-review-close">
            Close
          </Button>
          {!isApproved && canApprove && (
            <Button
              onClick={approve}
              disabled={busy || !rep.trim()}
              className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
              data-testid="rrsn-approve-btn"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Approve & Sign
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RailcarRepairStatusNotificationReviewModal;
